// Shared form-field builders used by every catalogue editor. Each one
// returns a labelled `<div class="field">` with the right input type
// inside. Keeps the form code in the screen modules focused on which
// fields a given entity needs, not on HTML wiring.
//
// All helpers fire `onChange` ONLY when the user produces a valid
// value (Number.isFinite for numbers, etc.) — invalid keystrokes pass
// through to the DOM but don't mutate the draft.

import { el } from "./dom.js";

export function textField(
  label: string,
  value: string,
  onChange: (v: string) => void,
  pattern?: string,
): HTMLElement {
  const input = el("input", {
    type: "text",
    value,
    ...(pattern ? { pattern } : {}),
    oninput: (e: Event) => onChange((e.target as HTMLInputElement).value),
  });
  return el("div", { class: "field" }, el("label", {}, label), input);
}

export function numberField(
  label: string,
  value: number,
  onChange: (v: number) => void,
  step?: number,
): HTMLElement {
  const input = el("input", {
    type: "number",
    value: String(value),
    step: step !== undefined ? String(step) : "1",
    oninput: (e: Event) => {
      const raw = (e.target as HTMLInputElement).value;
      const parsed = Number.parseFloat(raw);
      if (Number.isFinite(parsed)) onChange(parsed);
    },
  });
  return el("div", { class: "field" }, el("label", {}, label), input);
}

export function selectField<T extends string>(
  label: string,
  options: readonly T[],
  value: T,
  onChange: (v: T) => void,
): HTMLElement {
  const select = el("select", {
    onchange: (e: Event) => onChange((e.target as HTMLSelectElement).value as T),
  });
  for (const opt of options) {
    const o = el("option", { value: opt }, opt);
    if (opt === value) (o as HTMLOptionElement).selected = true;
    select.append(o);
  }
  return el("div", { class: "field" }, el("label", {}, label), select);
}

export function checkboxField(
  label: string,
  value: boolean,
  onChange: (v: boolean) => void,
): HTMLElement {
  const input = el("input", {
    type: "checkbox",
    onchange: (e: Event) => onChange((e.target as HTMLInputElement).checked),
  });
  if (value) (input as HTMLInputElement).checked = true;
  return el("div", { class: "field" }, el("label", {}, label), input);
}

export function readonlyField(label: string, value: string): HTMLElement {
  return el(
    "div",
    { class: "field" },
    el("label", {}, label),
    el("input", { type: "text", value, disabled: true }),
  );
}
